import { $, bindClick } from "../lib/dom.js";
import { escHtml } from "../lib/hints.js";
import { api } from "../api.js";
import { showBanner } from "./index.js";
import { initBrandAuthHandlers, loadBrandAuthSection } from "./brand-owner-auth.js";

const STATUS_LABEL = {
  pending: "待审核",
  active: "已通过",
  rejected: "已驳回",
  disabled: "已停用",
};

const KEY_FIELDS = [
  "brand-code",
  "brand-name",
  "brand-legal",
  "brand-contact",
  "brand-phone",
  "brand-license",
];

let brandListCache = [];

function canSubmitBrand() {
  if (!window.McAuth || !window.McAuth.hasPerm) return true;
  return window.McAuth.hasPerm("mc:brand:write");
}

function showBrandFormMsg(text, ok) {
  showBanner(text, ok);
  const el = $("brand-form-feedback");
  if (!el) return;
  el.textContent = text || "";
  el.classList.toggle("hidden", !text);
  el.classList.toggle("ok", !!ok);
  el.classList.toggle("err", !!text && !ok);
  if (text) {
    try {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch {
      /* ignore */
    }
  }
}

function showBrandView(view) {
  const list = $("brand-list-view");
  const reg = $("brand-register-view");
  if (list) list.classList.toggle("hidden", view !== "list");
  if (reg) reg.classList.toggle("hidden", view !== "register");
}

function brandCanEdit(brand) {
  if (!brand) return true;
  const st = brand.status || "pending";
  return st === "rejected" || st === "pending" || st === "disabled";
}

function renderBrandStatus(brand, readOnly) {
  const el = $("brand-status");
  const rej = $("brand-reject-reason");
  if (!el) return;
  if (!brand) {
    el.textContent = "填写品牌资料后提交审核";
    if (rej) rej.classList.add("hidden");
  } else {
    const st = brand.status || "pending";
    el.innerHTML = `状态：<strong>${STATUS_LABEL[st] || st}</strong>`;
    if (rej) {
      if (st === "rejected" && (brand.reject_reason_code || brand.reject_reason_text)) {
        rej.textContent = `驳回：${brand.reject_reason_code || ""} ${brand.reject_reason_text || ""}`.trim();
        rej.classList.remove("hidden");
      } else {
        rej.classList.add("hidden");
      }
    }
  }
  const canEdit = !readOnly && brandCanEdit(brand);
  KEY_FIELDS.forEach((id) => {
    const input = $(id);
    if (input) input.disabled = !canEdit;
  });
  const btn = $("btn-brand-submit");
  if (btn) {
    const st = brand && brand.status;
    btn.classList.toggle("hidden", !!readOnly || st === "active");
    const canWrite = canSubmitBrand();
    btn.disabled = !canEdit || !canWrite;
    if (!readOnly) {
      btn.textContent = st === "rejected" ? "重新提交" : st === "pending" ? "更新并提交" : "提交品牌登记";
      btn.title = canWrite ? "" : "当前账号无品牌登记提交权限（mc:brand:write）";
    }
  }
  if (!readOnly && brandCanEdit(brand) && !canSubmitBrand()) {
    showBrandFormMsg(
      "当前账号无品牌登记提交权限（mc:brand:write）。请联系商家管理员在控制台分配「入驻办理」或「品牌商家」相关权限。",
      false
    );
  }
  const title = $("brand-register-title");
  const desc = $("brand-register-desc");
  if (title) {
    title.textContent = readOnly ? "品牌详情" : brand && brand.brand_id ? "品牌登记 / 补件" : "品牌登记";
  }
  if (desc) {
    desc.textContent = readOnly
      ? "已通过审核的品牌关键信息不可修改。"
      : "提交品牌资料，等待平台审核。审核通过后将自动授予「品牌商家」角色。";
  }
}

function fillBrandForm(brand, readOnly) {
  if ($("brand-edit-id")) $("brand-edit-id").value = (brand && brand.brand_id) || "";
  if ($("brand-code")) $("brand-code").value = (brand && brand.brand_code) || "";
  if ($("brand-name")) $("brand-name").value = (brand && brand.brand_name) || "";
  if ($("brand-legal")) $("brand-legal").value = (brand && brand.legal_entity) || "";
  if ($("brand-contact")) $("brand-contact").value = (brand && brand.contact_name) || "";
  if ($("brand-phone")) $("brand-phone").value = (brand && brand.contact_phone) || "";
  if ($("brand-license")) $("brand-license").value = (brand && brand.license_doc_url) || "";
  renderBrandStatus(brand, readOnly);
}

function statusTagClass(status) {
  if (status === "active") return "brand-tag ok";
  if (status === "rejected" || status === "disabled") return "brand-tag warn";
  return "brand-tag";
}

function renderBrandList(items) {
  const host = $("brand-list-host");
  const status = $("brand-list-status");
  if (!host) return;
  brandListCache = items || [];
  if (status) {
    status.textContent = brandListCache.length
      ? `共 ${brandListCache.length} 个品牌`
      : "尚未登记品牌，可点击「品牌登记」提交首个品牌。";
  }
  if (!brandListCache.length) {
    host.innerHTML = "<p class='mc-desc muted'>暂无品牌记录。</p>";
    return;
  }
  const rows = brandListCache
    .map((b) => {
      const st = b.status || "pending";
      const readonly = st === "active";
      const action = readonly ? "查看" : "补件";
      return `<tr class="brand-row" data-id="${escHtml(b.brand_id)}">
        <td><code>${escHtml(b.brand_code || "")}</code></td>
        <td><strong>${escHtml(b.brand_name || "")}</strong></td>
        <td><span class="${statusTagClass(st)}">${escHtml(STATUS_LABEL[st] || st)}</span></td>
        <td>${escHtml(b.legal_entity || "—")}</td>
        <td>${escHtml(b.contact_name || "—")}</td>
        <td>${escHtml(b.contact_phone || "—")}</td>
        <td class="muted">${escHtml((b.updated_at || b.submitted_at || "").slice(0, 16) || "—")}</td>
        <td><button type="button" class="secondary sm btn-brand-open" data-id="${escHtml(b.brand_id)}">${action}</button></td>
      </tr>`;
    })
    .join("");
  host.innerHTML = `<div class="brand-table-wrap"><table class="brand-table">
    <thead><tr>
      <th>品牌编码</th><th>品牌名称</th><th>状态</th><th>主体</th><th>联系人</th><th>电话</th><th>更新</th><th></th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
  host.querySelectorAll(".btn-brand-open").forEach((btn) => {
    btn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      openBrandRegister(btn.getAttribute("data-id"));
    });
  });
  host.querySelectorAll(".brand-row").forEach((row) => {
    row.addEventListener("click", () => {
      openBrandRegister(row.getAttribute("data-id"));
    });
  });
}

async function loadBrandList() {
  const status = $("brand-list-status");
  if (status) status.textContent = "加载中…";
  const { ok, data } = await api("GET", "/api/v1/fitting/mall/brands");
  if (!ok) {
    if (status) status.textContent = "品牌列表加载失败";
    showBanner("品牌列表加载失败", false);
    return;
  }
  renderBrandList((data && data.items) || []);
}

function openBrandRegister(brandId) {
  const brand = brandId
    ? brandListCache.find((b) => b.brand_id === brandId) || null
    : null;
  const readOnly = !!(brand && brand.status === "active");
  fillBrandForm(brand, readOnly);
  showBrandView("register");
}

export async function loadBrandPanel() {
  showBrandView("list");
  await loadBrandList();
  // M8-26：同一页的下两块（申请品牌授权 / 授权申请审核）。
  // 审核块只有品牌所有者有内容 —— 空列表时它显示「还没有商家申请」，不是隐藏，
  // 这样非所有者也能明白这一块是什么、为什么自己不在这里审批。
  await loadBrandAuthSection();
}

async function submitBrand() {
  if (!canSubmitBrand()) {
    showBrandFormMsg(
      "当前账号无品牌登记提交权限（mc:brand:write），无法提交。请换商家管理员账号或联系平台运营授权。",
      false
    );
    return;
  }
  const btn = $("btn-brand-submit");
  const brandId = ($("brand-edit-id") && $("brand-edit-id").value.trim()) || null;
  const body = {
    brand_id: brandId || undefined,
    brand_code: ($("brand-code") && $("brand-code").value.trim()) || "",
    brand_name: ($("brand-name") && $("brand-name").value.trim()) || "",
    legal_entity: ($("brand-legal") && $("brand-legal").value.trim()) || null,
    contact_name: ($("brand-contact") && $("brand-contact").value.trim()) || null,
    contact_phone: ($("brand-phone") && $("brand-phone").value.trim()) || null,
    license_doc_url: ($("brand-license") && $("brand-license").value.trim()) || null,
  };
  if (!body.brand_code || !body.brand_name) {
    showBrandFormMsg("品牌编码与名称必填", false);
    return;
  }
  const prevLabel = btn && btn.textContent;
  if (btn) {
    btn.disabled = true;
    btn.textContent = "提交中…";
  }
  showBrandFormMsg("正在提交…", true);
  const { ok, data, status } = await api("PUT", "/api/v1/fitting/mall/brand/register", { body });
  if (btn) {
    btn.disabled = false;
    if (prevLabel) btn.textContent = prevLabel;
  }
  if (ok) {
    showBrandFormMsg("品牌登记已提交，等待平台审核", true);
    showBrandView("list");
    await loadBrandList();
  } else {
    const msg =
      (data && data.error && data.error.message) ||
      (data && data.message) ||
      (status === 0 ? "网络错误或接口不可达，请检查登录状态与 API 地址" : "提交失败");
    showBrandFormMsg(msg, false);
  }
}

export function initBrandHandlers() {
  bindClick("btn-brand-refresh", async () => {
    await loadBrandList();
    await loadBrandAuthSection();
  });
  initBrandAuthHandlers();
  bindClick("btn-brand-register-new", () => openBrandRegister(null));
  bindClick("btn-brand-back-list", () => {
    showBrandView("list");
    loadBrandList();
  });
  bindClick("btn-brand-submit", () => submitBrand());
}
